/**
 * Feature flags hook + <FeatureGate> wrapper.
 *
 * Polls GET /api/features every 60s. Renders nothing while loading the
 * first time so flags can't flicker on. Defaults to `true` for any flag
 * the backend doesn't yet know about — flags are kill-switches, not
 * feature-launch gates, per Q3 (kill-switch only).
 *
 * Usage:
 *   const flags = useFeatureFlags();
 *   if (flags.career_coach) { ... }
 *
 *   <FeatureGate flag="career_coach"><CareerCoach/></FeatureGate>
 *   <FeatureGate flag="career_coach" fallback={<Disabled/>}>...</FeatureGate>
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE as API_URL } from './apiBase';

const POLL_INTERVAL_MS = 60_000;
const DEFAULTS = {
  career_coach: true,
  reoptimize: true,
  job_match_score: true,
  exit_intent_modal: true,
  ats_check_score_first_gate: true,
  winback_coupon: true,
};

let _cache = { ...DEFAULTS };
let _listeners = new Set();
let _pollTimer = null;
let _hasLoaded = false;

async function fetchFlags() {
  try {
    const res = await axios.get(`${API_URL}/api/features`);
    const next = { ...DEFAULTS, ...(res?.data?.flags || {}) };
    _cache = next;
    _hasLoaded = true;
    _listeners.forEach((cb) => cb(next));
  } catch {
    // Network blip → keep last-known flags. If we never loaded, stay on
    // optimistic DEFAULTS so we never accidentally hide working features.
  }
}

function startPolling() {
  if (_pollTimer) return;
  fetchFlags();
  _pollTimer = setInterval(fetchFlags, POLL_INTERVAL_MS);
}

export function useFeatureFlags() {
  const [flags, setFlags] = useState(_cache);
  useEffect(() => {
    _listeners.add(setFlags);
    startPolling();
    return () => { _listeners.delete(setFlags); };
  }, []);
  return flags;
}

export function useFeatureFlag(name) {
  const flags = useFeatureFlags();
  // If the backend knows the flag, trust it. Otherwise return DEFAULTS[name]
  // (which is `true` for everything we ship today — flags are kill-switches).
  return Object.prototype.hasOwnProperty.call(flags, name) ? !!flags[name] : true;
}

export function FeatureGate({ flag, fallback = null, children }) {
  const enabled = useFeatureFlag(flag);
  return enabled ? children : fallback;
}

// Imperative refresh for admin tools or post-toggle UX.
export const refreshFeatureFlags = useCallback ? null : null; // tree-shake guard
export async function manuallyRefreshFlags() { await fetchFlags(); }

// Snapshot for debugging — never trust this during render.
export function _peekFlags() { return { ..._cache, _hasLoaded }; }
